import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";

import { useLoadingScreen } from '../../../Store/LoadingManager';

import QueryBuilderHelper from "../../../Utilities/Helpers/QueryBuilderHelper";
import DropdownOptionsHelper from "../../../Utilities/Helpers/DropdownOptionsHelper";

import { CachingEnum } from '../../../Utilities/Enums/CachingEnum';

import { GetRoles, GetRoleById, CreateRoleService, EditRoleService, DeleteRoleService, GetRolesAccessibilityByRoleId, UpsertRoleAccessibilitiesService } from "../../../Api/Service/Maintenance/Accessibilities/rolesService";

import { ErrorMessage, SuccessMessage } from "../../../Constants/Messages";
import { LegendItemInterface } from "../../../Models/Common/List/LegendInterface";
import { ActiveStatus, ActiveStatusEnum } from "../../../Utilities/Enums/StatusEnums";

export function useRolesHook(q_queryKey: any, params: any, runInitialFetch: boolean = true) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const DropdownList = [CachingEnum.COUNTRY_LIST, CachingEnum.ADMIN_MODULES, CachingEnum.STATUS_LIST, CachingEnum.ROLE_TYPE_LIST];
    let { loading, setLoading } = useLoadingScreen();

    // Initial fetch
    const { data, fetchStatus } = useQuery({
        queryKey: q_queryKey,
        queryFn: () => getRolesList(params),
        enabled: runInitialFetch,
    });

    const { data: DropdownData, pending: dropdownFetchStatus } = useQueries({
        queries: DropdownList.map((item) => {
            return {
                queryKey: [item],
                queryFn: () => DropdownOptionsHelper.getLists(item),
            }
        }),
        combine: (results) => {
            const dictionary = Object.assign({}, ...results.map((x, index) => ({ [DropdownList[index]]: x.data })));
            return {
                data: dictionary,
                pending: results.some((result) => result.isPending),
            }
        },
    })

    const getRolesList = async (params: any) => {
        let _data;
        let _legends;
        const responseData = await GetRoles(QueryBuilderHelper.BuildQueryString(params));
        if (responseData?.isSuccess) {
            _data = responseData?.data
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        _legends = finalizeLegend(_data)
        return { ..._legends, data: _data?.data}
    }

    const finalizeLegend = (_data: any) => {
        const countDict = _data?.data?.metaData?.statusCount
        const totalCount = countDict?.find((x: any | null) => x?.status == '-1')?.count ?? 0
        const legends = ([
            {
                ...ActiveStatus[ActiveStatusEnum.ALL],
                count: totalCount
            },
            {
                ...ActiveStatus[ActiveStatusEnum.ACTIVE],
                count: countDict?.find((x: any | null) => x?.status == ActiveStatusEnum.ACTIVE)?.count ?? 0
            },
            {
                ...ActiveStatus[ActiveStatusEnum.INACTIVE],
                count: countDict?.find((x: any | null) => x?.status == ActiveStatusEnum.INACTIVE)?.count ?? 0
            },
        ])
        return { legends: legends, totalCount: totalCount}
    }

    const getRoleById = async (id: string | number) => {
        setLoading(true);
        
        const responseData = await GetRoleById(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const getRolesAccessibilityByRoleId = async (id: string | number) => {
        setLoading(true);

        const responseData = await GetRolesAccessibilityByRoleId(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const createRole = async (params: any) => {
        setLoading(true);
        const responseData = await CreateRoleService(params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Role " + SuccessMessage.CREATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const upsertRoleAccessibilities = async (id: any, params: any) => {
        setLoading(true);
        const responseData = await UpsertRoleAccessibilitiesService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Role Accessibilities " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const editRoleById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await EditRoleService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Role " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const deleteRoleById = async (id: any, remarks?: any) => {
        setLoading(true);

        const responseData = await DeleteRoleService(id, JSON.stringify({remarks: remarks ?? ""}));

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Role " + SuccessMessage.DELETED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_DELETING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    return { data, DropdownData, fetchStatus, dropdownFetchStatus, getRoleById, createRole, editRoleById, deleteRoleById, getRolesAccessibilityByRoleId, upsertRoleAccessibilities }
}